import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, MenuItem, Select } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { extractFilterListPayload } from "../../hooks/useAdvancedSearchFilterOptions";
import { useCachedFilterOptions } from "../../hooks/useCachedFilterOptions";
import {
  EMPTY_CLIENTS_FILTER_STATE,
  getClientsDateFieldOptions,
  getClientsPotentialOptions,
  normalizeClientsFilterState,
} from "../../utils/dashboardClientsFilterState";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

async function fetchClientsFilterOptions() {
  const results = await Promise.allSettled([
    fetchData("/clients-groups?limit=200", "GET"),
    fetchData("/client-tags?limit=200&includeUsed=1", "GET"),
    fetchData("/dashboard/clientes/filter-options", "GET"),
  ]);
  const [grp, tagRes, geo] = results.map((r) => (r.status === "fulfilled" ? r.value : null));
  const tagList = extractFilterListPayload(tagRes)
    .map((row) => {
      const nome = String(row?.nome || row?.name || row?.tag || "").trim();
      return { id: nome || String(row?.id || ""), nome: nome || String(row?.id || "") };
    })
    .filter((row) => row.id);
  const geoPayload = geo?.data ?? geo ?? {};
  return {
    groups: extractFilterListPayload(grp),
    tagList,
    ufs: Array.isArray(geoPayload.ufs) ? geoPayload.ufs : [],
    cidades: Array.isArray(geoPayload.cidades) ? geoPayload.cidades : [],
  };
}

export default function DashboardClientsFiltersModal({
  open,
  onClose,
  initialState = EMPTY_CLIENTS_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const { data: options, isLoading: loading } = useCachedFilterOptions(
    "dashboard-clientes",
    fetchClientsFilterOptions,
    { enabled: open }
  );
  const groups = options?.groups || [];
  const ufs = options?.ufs || [];
  const cidades = options?.cidades || [];
  const tags = useMemo(() => {
    const tagList = options?.tagList || [];
    return [
      {
        id: "notag",
        nome: t("dashboardPage.clientsFilters.noTag", { defaultValue: "Sem tag" }),
      },
      ...tagList,
    ];
  }, [options?.tagList, t]);
  const [groupIds, setGroupIds] = useState([]);
  const [ufIds, setUfIds] = useState([]);
  const [cidadeIds, setCidadeIds] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [potencialIds, setPotencialIds] = useState([]);
  const [dateField, setDateField] = useState("cadastro");
  const [allPeriod, setAllPeriod] = useState(false);
  const prevOpen = React.useRef(false);

  const potentialOptions = useMemo(() => getClientsPotentialOptions(), []);
  const dateFieldOptions = useMemo(() => getClientsDateFieldOptions(), []);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizeClientsFilterState(initialState);
      setGroupIds(s.ids_grupos);
      setUfIds(s.ufs);
      setCidadeIds(s.cidades);
      setTagIds(s.tags);
      setPotencialIds(s.ids_potenciais);
      setDateField(s.date_field);
      setAllPeriod(s.all_period);
    }
  }, [open, initialState]);

  const buildLabels = () => {
    const groupLabels = {};
    groups.forEach((g) => {
      const id = Number(g?.id);
      if (!id) return;
      groupLabels[String(id)] = String(g?.nome || g?.name || `#${id}`);
    });
    const ufLabels = {};
    ufs.forEach((u) => {
      const id = String(u?.id || u?.nome || "").trim();
      if (!id) return;
      ufLabels[id] = String(u?.nome || id);
    });
    const cityLabels = {};
    cidades.forEach((c) => {
      const id = String(c?.id || c?.nome || "").trim();
      if (!id) return;
      cityLabels[id] = String(c?.nome || id);
    });
    const tagLabels = {};
    tags.forEach((tag) => {
      const id = String(tag?.id || "").trim();
      if (!id) return;
      tagLabels[id] = String(tag?.nome || id);
    });
    const potentials = {};
    potentialOptions.forEach((o) => {
      potentials[String(o.id)] = o.label;
    });
    return {
      groups: groupLabels,
      ufs: ufLabels,
      cities: cityLabels,
      tags: tagLabels,
      potentials,
    };
  };

  const handleClear = () => {
    setGroupIds([]);
    setUfIds([]);
    setCidadeIds([]);
    setTagIds([]);
    setPotencialIds([]);
    setDateField("cadastro");
    setAllPeriod(false);
  };

  const handleApply = () => {
    onApply?.(
      normalizeClientsFilterState({
        ids_grupos: groupIds,
        ufs: ufIds,
        cidades: cidadeIds,
        tags: tagIds,
        ids_potenciais: potencialIds,
        date_field: dateField,
        all_period: allPeriod,
        labels: buildLabels(),
      })
    );
    onClose?.();
  };

  const hasActive =
    groupIds.length > 0 ||
    ufIds.length > 0 ||
    cidadeIds.length > 0 ||
    tagIds.length > 0 ||
    potencialIds.length > 0 ||
    dateField !== "cadastro" ||
    allPeriod;

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<PeopleAltOutlinedIcon />}
      title={t("dashboardPage.clientsFilters.title", { defaultValue: "Filtrar clientes" })}
      subtitle={t("dashboardPage.clientsFilters.subtitle", {
        defaultValue: "KPIs e demografia passam a refletir só este público",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.clientsFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.clientsFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.clientsFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.groups", { defaultValue: "Grupo / nível" })}
        hint={t("dashboardPage.clientsFilters.groupsHint", {
          defaultValue: "Nível de consciência",
        })}
        selectedCount={groupIds.length}
      >
        <DashboardMultiPickList
          options={groups}
          value={groupIds}
          onChange={setGroupIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.clientsFilters.searchGroups", {
            defaultValue: "Buscar grupos…",
          })}
          emptyLabel={t("dashboardPage.clientsFilters.emptyGroups", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.ufs", { defaultValue: "UF" })}
        selectedCount={ufIds.length}
      >
        <DashboardMultiPickList
          options={ufs}
          value={ufIds}
          onChange={setUfIds}
          getLabel={(o) => o?.nome || o?.id}
          getValue={(o) => o?.id || o?.nome}
          valueAsString
          searchPlaceholder={t("dashboardPage.clientsFilters.searchUfs", {
            defaultValue: "Buscar UFs…",
          })}
          emptyLabel={t("dashboardPage.clientsFilters.emptyUfs", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.cities", { defaultValue: "Cidade" })}
        selectedCount={cidadeIds.length}
      >
        <DashboardMultiPickList
          options={cidades}
          value={cidadeIds}
          onChange={setCidadeIds}
          getLabel={(o) => o?.nome || o?.id}
          getValue={(o) => o?.id || o?.nome}
          valueAsString
          searchPlaceholder={t("dashboardPage.clientsFilters.searchCities", {
            defaultValue: "Buscar cidades…",
          })}
          emptyLabel={t("dashboardPage.clientsFilters.emptyCities", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.tags", { defaultValue: "Tags" })}
        selectedCount={tagIds.length}
      >
        <DashboardMultiPickList
          options={tags}
          value={tagIds}
          onChange={setTagIds}
          getLabel={(o) => o?.nome || o?.id}
          getValue={(o) => o?.id}
          valueAsString
          searchPlaceholder={t("dashboardPage.clientsFilters.searchTags", {
            defaultValue: "Buscar tags…",
          })}
          emptyLabel={t("dashboardPage.clientsFilters.emptyTags", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.potential", { defaultValue: "Potencial" })}
        selectedCount={potencialIds.length}
      >
        <DashboardMultiPickList
          options={potentialOptions}
          value={potencialIds}
          onChange={setPotencialIds}
          getLabel={(o) => o.label}
          getValue={(o) => o.id}
          searchPlaceholder={t("dashboardPage.clientsFilters.searchPotential", {
            defaultValue: "Buscar potencial…",
          })}
          emptyLabel={t("dashboardPage.clientsFilters.emptyPotential", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.clientsFilters.dateField", { defaultValue: "Campo de data" })}
        hint={t("dashboardPage.clientsFilters.dateFieldHint", {
          defaultValue: "Qual data o período da barra deve usar",
        })}
      >
        <Select
          fullWidth
          size="small"
          value={dateField}
          onChange={(e) => setDateField(e.target.value)}
          disabled={allPeriod}
          sx={{ borderRadius: 2 }}
        >
          {dateFieldOptions.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              {t(`dashboardPage.clientsFilters.${opt.labelKey}`, { defaultValue: opt.fallback })}
            </MenuItem>
          ))}
        </Select>
        <FormControlLabel
          sx={{ mt: 1, ml: 0 }}
          control={
            <Checkbox
              checked={allPeriod}
              onChange={(e) => setAllPeriod(e.target.checked)}
              size="small"
            />
          }
          label={t("dashboardPage.clientsFilters.allPeriod", {
            defaultValue: "Ignorar período da barra (todo o histórico)",
          })}
        />
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="clientes"
          getSnapshot={() =>
            normalizeClientsFilterState({
              ids_grupos: groupIds,
              ufs: ufIds,
              cidades: cidadeIds,
              tags: tagIds,
              ids_potenciais: potencialIds,
              date_field: dateField,
              all_period: allPeriod,
              labels: buildLabels(),
            })
          }
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
